import {
  Button,
  DialogActions,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import _ from "lodash";
import { useEffect, useState } from "react";
import CustomDialog from "../../../../utils/CustomDialog";
import ClearIcon from "@mui/icons-material/Clear";
import { TemplateType, templateMaxSelectedSetItems } from "./utils/constants";
import { hasReachedMaxItems, exceedsMaxItems, extractItems, procedureTypeFromTemplateType } from "./utils";
import CustomAlert from "../../../../utils/Alert";
import { MAX_ORDERS } from "../../../homologar/orders/utils/constants";

const SetContentPicker = ({
  selectedTemplate,
  setSelectedTemplate,
  addOrderItems,
  orderType,
  templateItemsFitInOrder,
}) => {
  const [open, setOpen] = useState(true);
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [search, setSearch] = useState("");
  const [warning, setWarning] = useState();
  const toggleOpen = () => setOpen((open) => !open);
  const options = extractItems(selectedTemplate, orderType).map(
    (item, idx) => ({
      id: idx,
      label: item.name,
      procedureId: item.procedureId,
      procedureType: item.procedureType,
      chapter: item.chapter,
    })
  );
  const maxItems = templateMaxSelectedSetItems[orderType];

  useEffect(() => {
    if (_.size(search) < 3) {
      setFilteredOptions(options);
      return;
    }

    const filtered = options.filter((option) => {
      return option.label.toLowerCase().includes(search.toLowerCase());
    });
    setFilteredOptions(filtered);
  }, [options, search, selectedTemplate]);

  useEffect(() => {
    if (!open) {
      setSelectedTemplate(null);
      setSelectedOptions([]);
      setSearch("");
      setFilteredOptions([]);
    }
  }, [open]);

  const toSelectedItems = (optionIds) =>
    options
      .filter((o) => optionIds.includes(o.id))
      .map((option) => ({
        name: option.label,
        procedureId: option.procedureId,
        procedureType: option.procedureType,
        chapter: option.chapter,
      }));
  const fitsInOrder = (optionIds) =>
    templateItemsFitInOrder(toSelectedItems(optionIds), selectedTemplate);
  const isOverLimit = (optionIds) =>
    templateItemsFitInOrder ? !fitsInOrder(optionIds) : exceedsMaxItems(maxItems, optionIds);
  const isLimitReached = (optionIds) =>
    templateItemsFitInOrder
      ? options.some((o) => !optionIds.includes(o.id) && !fitsInOrder([...optionIds, o.id]))
      : hasReachedMaxItems(maxItems, optionIds);
  const isBlocked = (optionId) =>
    !isChecked(optionId) &&
    (templateItemsFitInOrder
      ? !fitsInOrder([...selectedOptions, optionId])
      : hasReachedMaxItems(maxItems, selectedOptions));

  const onAccept = () => {
    if (isOverLimit(selectedOptions)) {
      setWarningMessage();
      return;
    }

    const selectedItems = toSelectedItems(selectedOptions);

    addOrderItems(selectedItems, selectedTemplate);
    toggleOpen();
    setSelectedTemplate(null);
  };

  const handleClearSearch = () => setSearch("");
  const isChecked = (optionId) => selectedOptions.includes(optionId);
  const setWarningMessage = () =>
    setWarning(
      templateItemsFitInOrder
        ? `Has alcanzado el máximo de ${MAX_ORDERS} órdenes por solicitud.`
        : `Has alcanzado el máximo de ${maxItems} elementos seleccionados.`
    );

  const handleCheckboxChange = (optionId, isChecked) => {
    setSelectedOptions((prev) => {
      if (isChecked) {
        const newSelectedOptions = [...prev, optionId];
        /**
         * Notice the user that the max items have been reached
         */
        if (isLimitReached(newSelectedOptions)) {
          setWarningMessage();
        }
        return newSelectedOptions;
      } else {
        return prev.filter((id) => id !== optionId);
      }
    });
  };

  return (
    <CustomDialog
      onClose={toggleOpen}
      open={open}
      title={"Seleccione las opciones a utilizar"}
      heigth={"80vh"}
      isBottom={true}
      size={"md"}
      Actions={
        <DialogActions
          sx={{
            position: "sticky",
            bottom: 0,
            backgroundColor: "white",
            display: "flex",
            justifyContent: "center",
            p: 1,
          }}
        >
          <Button variant="contained" onClick={onAccept}>
            Aceptar
          </Button>
        </DialogActions>
      }
    >
      <Stack p="1rem" spacing="1rem">
        <TextField
          type="text"
          placeholder="Search..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="form-control mt-3"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={handleClearSearch} edge="end">
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <Grid container>
          {_.map(filteredOptions, (option) => (
            <Grid
              item
              xs={12}
              sm={6}
              key={option.id}
              style={{ wordBreak: "break-word" }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    name={`options[${option.id}]`}
                    onChange={(e) =>
                      handleCheckboxChange(option.id, e.target.checked)
                    }
                    checked={isChecked(option.id)}
                    disabled={isBlocked(option.id)}
                  />
                }
                label={option.label}
              />
            </Grid>
          ))}
        </Grid>
      </Stack>
      <CustomAlert
        severity="warning"
        show={!_.isEmpty(warning)}
        onClose={() => setWarning(null)}
        message={warning}
      />
    </CustomDialog>
  );
};

const TemplateContentPicker = (props) => {
  const { selectedTemplate, addOrderItems, setSelectedTemplate, orderType } =
    props;

  const resolvedOrderType =
    selectedTemplate?.procedureType ??
    procedureTypeFromTemplateType(selectedTemplate?.type) ??
    orderType;

  useEffect(() => {
    if (
      !_.isEmpty(selectedTemplate) &&
      selectedTemplate.inputMethod === TemplateType.TEXT
    ) {
      const items = extractItems(selectedTemplate, resolvedOrderType) ?? [];
      addOrderItems(items, selectedTemplate);
      setSelectedTemplate(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplate, addOrderItems, setSelectedTemplate, orderType]);

  if (_.isEmpty(selectedTemplate)) return null;

  if (selectedTemplate.inputMethod === TemplateType.TEXT) {
    return null;
  }

  return <SetContentPicker {...props} orderType={resolvedOrderType} />;
};

export default TemplateContentPicker;
